import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import DocumentDropZone from './DocumentDropZone.jsx';
import DocumentGallery from './DocumentGallery.jsx';
import { documentAPI } from '../../api.js';

/**
 * UnifiedDocumentsTab - Reusable Documents tab for Driver and Vehicle detail views
 *
 * Provides consistent UI/UX for document management across entities.
 *
 * @param {Object} props
 * @param {'DRIVER'|'VEHICLE'} props.scope - Entity scope
 * @param {string} props.referenceId - Formatted reference ID (DRV_000123 or VHC_000456)
 * @param {string} props.title - Tab title (e.g., "Documents du Conducteur")
 * @param {string[]} props.allowedTypes - Document types allowed for upload
 * @param {string[]} props.acceptedTypeLabels - Human-readable labels for accepted types
 * @param {any[]} props.documents - Current document list
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onRefresh - Callback to reload documents
 * @param {Function} props.onUploadComplete - Callback when upload finishes: (docs) => void
 * @param {Function} [props.onMessage] - Callback for toast messages: ({title, message, type}) => void
 * @param {Function} [props.onValidate] - Callback to open validation modal: (doc) => void
 * @param {number} [props.maxFiles=10] - Maximum files per upload
 * @param {number} [props.maxSizeBytes=52428800] - Maximum file size (default 50MB)
 * @param {Object} [props.entityData] - Entity data for validation context
 */
function UnifiedDocumentsTab({
  scope,
  referenceId,
  title,
  allowedTypes,
  acceptedTypeLabels,
  documents,
  loading,
  onRefresh,
  onUploadComplete,
  onMessage,
  onValidate,
  maxFiles = 10,
  maxSizeBytes = 50 * 1024 * 1024,
  entityData
}) {
  const { t } = useTranslation();
  const [showUpload, setShowUpload] = useState(false);

  // Handle upload complete
  const handleUploadComplete = (uploadedDocs) => {
    setShowUpload(false);
    onRefresh?.();
    onUploadComplete?.(uploadedDocs);
    onMessage?.({
      title: t('common.tabs.documentsTab.uploadedTitle'),
      message: t('common.tabs.documentsTab.uploadedMessage', { count: uploadedDocs.length }),
      type: 'success'
    });
  };

  // Handle upload error
  const handleUploadError = (error) => {
    onMessage?.({
      title: t('common.messages.errorTitle'),
      message: error.message || t('documents.messages.uploadFailedShort'),
      type: 'error'
    });
  };

  // Handle document click (download)
  const handleDocumentClick = (doc) => {
    documentAPI.download(doc.id, doc.original_filename || doc.filename);
  };

  return (
    <div className="space-y-6">
      {/* Header with upload toggle */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showUpload
              ? 'bg-gray-200 text-gray-700'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Upload className="w-4 h-4" />
          {showUpload ? t('common.tabs.documentsTab.closeButton') : t('documents.list.addDocument')}
        </button>
      </div>

      {/* Document Upload Zone */}
      {showUpload && (
        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
          <DocumentDropZone
            scope={scope}
            referenceId={referenceId}
            allowedTypes={allowedTypes}
            maxFiles={maxFiles}
            maxSizeBytes={maxSizeBytes}
            onUploadComplete={handleUploadComplete}
            onError={handleUploadError}
          />
        </div>
      )}

      {/* Document Gallery */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <DocumentGallery
          scope={scope}
          referenceId={referenceId}
          documents={documents}
          loading={loading}
          onDocumentClick={handleDocumentClick}
          onDelete={onRefresh}
          onRefresh={onRefresh}
          onValidate={onValidate}
        />
      </div>

      {/* Document Types Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-amber-800 mb-2">{t('common.tabs.documentsTab.acceptedTypesTitle')}</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-amber-700">
          {acceptedTypeLabels.map((label, index) => (
            <span key={index}>• {label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default UnifiedDocumentsTab;
